import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Lock, Unlock, RotateCw } from "lucide-react";
import useResourceLock from "../../hooks/useResourceLock";
import type { Lockable, LockFileType, LockResourceType } from "../../types";

interface LockButtonProps {
  resourceType: LockResourceType;
  resourceId?: string;
  /** The row itself, or just its lock fields. */
  resource?: Lockable | null;
  /** Human name shown in the confirmation copy. */
  resourceName?: string;
  /**
   * Who created the resource: `created_by` for a BrainBank, `user_id` for a VA,
   * `uploaded_by_id` for a file. The button hides unless this is the current
   * admin -- they may only lock what they created.
   */
  ownerId?: string | null;
  /** Required when resourceType is "file". */
  fileType?: LockFileType;
  /** Called after a successful lock/unlock so the list can refetch. */
  onChange?: () => void;
  className?: string;
}

const NOUNS: Record<LockResourceType, string> = {
  brainbank: "BrainBank",
  va: "Virtual Assistant",
  file: "file",
};

/**
 * Lock / unlock control for a single row.
 *
 * Renders nothing unless the current user is an admin *and* created this
 * resource. Both checks are presentation only -- the API enforces them
 * independently, so hiding the button is convenience, not security.
 *
 * Locking asks for confirmation because it is not a private setting: it takes
 * the resource away from every other user in the org, including other admins.
 * Unlocking is a single click, being the reversible direction.
 */
const LockButton: React.FC<LockButtonProps> = ({
  resourceType,
  resourceId,
  resource,
  resourceName,
  ownerId,
  fileType,
  onChange,
  className = "",
}) => {
  const { isLocked, canLock, busy, lock, unlock, lockedLabel } = useResourceLock({
    resourceType,
    resourceId,
    resource,
    ownerId,
    fileType,
    onChange,
  });
  /**
   * What the user actually clicked, captured at click time.
   *
   * Not read from props inside the dialog: the list behind it can refetch or
   * reorder while the dialog is open, and React reuses a row's component
   * instance when that happens. Reading props at confirm time would then show
   * one VA's name while locking a different VA. Snapshotting both the id and
   * the name keeps the prompt and the action describing the same thing.
   */
  const [pending, setPending] = useState<{ id: string; name?: string } | null>(null);

  if (!canLock) return null;

  const noun = NOUNS[resourceType];
  const label = isLocked ? `Unlock ${noun}` : `Lock ${noun}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy || !resourceId) return;
    if (isLocked) {
      unlock(resourceId);
    } else {
      setPending({ id: resourceId, name: resourceName });
    }
  };

  const confirmLock = async () => {
    const target = pending;
    setPending(null);
    if (target) await lock(target.id);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-label={label}
        title={isLocked ? lockedLabel || label : label}
        className={`p-1 rounded-full focus:outline-none disabled:opacity-50 ${
          isLocked
            ? "text-amber-600 hover:text-amber-800"
            : "text-gray-500 hover:text-gray-700"
        } ${className}`}
      >
        {busy ? (
          <RotateCw className="h-4 w-4 animate-spin" />
        ) : isLocked ? (
          <Lock className="h-4 w-4" />
        ) : (
          <Unlock className="h-4 w-4" />
        )}
      </button>

      <Transition appear show={Boolean(pending)} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setPending(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="flex items-center text-lg font-medium leading-6 text-gray-900"
                  >
                    <Lock className="mr-2 h-5 w-5 text-amber-600" />
                    Lock this {noun}?
                  </Dialog.Title>

                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>
                      {pending?.name ? (
                        <>
                          <span className="font-medium text-gray-900">
                            {pending.name}
                          </span>{" "}
                          will be visible to you only.
                        </>
                      ) : (
                        <>This {noun} will be visible to you only.</>
                      )}
                    </p>
                    <p>
                      Other administrators lose access to it. You can keep using
                      it exactly as you do now.
                    </p>
                    <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                      Anyone it is currently shared with{" "}
                      {resourceType === "va" ? "and any managers " : ""}
                      will lose access, and will be notified. You can share it
                      again after locking if you want them to keep access.
                    </p>
                    <p>Only you can unlock it afterwards.</p>
                  </div>

                  <div className="mt-6 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setPending(null)}
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmLock}
                      className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none"
                    >
                      Lock
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default LockButton;
